import { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { Warning, Info } from '@phosphor-icons/react';

const tl = (v) => (v === null || v === undefined ? '—' : formatCurrency(v));
const pctT = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`);
const errText = (e) => e.response?.data?.detail || e.message || 'İşlem başarısız';
const short = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v);

const MiniTable = ({ title, rows, testid, total }) => (
  <div data-testid={testid}>
    <p className="text-sm font-medium mb-1">{title}</p>
    {rows.length === 0 ? <p className="text-sm text-muted-foreground">POS raporu yüklenmedi</p> : (
      <Table className="text-sm"><TableBody>
        {rows.map((r) => (
          <TableRow key={r.label}><TableCell>{r.label}</TableCell><TableCell className="text-right">{tl(r.amount)}</TableCell><TableCell className="text-right text-xs text-muted-foreground">{total ? `%${((r.amount / total) * 100).toFixed(1)}` : ''}</TableCell></TableRow>
        ))}
      </TableBody></Table>
    )}
  </div>
);

const ReconTable = ({ rows }) => (
  <div className="overflow-x-auto max-h-80 overflow-y-auto" data-testid="pos-recon-table">
    <Table className="text-sm">
      <TableHeader><TableRow><TableHead>Tarih</TableHead><TableHead className="text-right">POS net satış</TableHead><TableHead className="text-right">ERP ciro</TableHead><TableHead className="text-right">Fark (ERP−POS)</TableHead><TableHead className="text-right">Fark %</TableHead><TableHead>Durum</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.date} data-testid={`pos-recon-row-${r.date}`}>
            <TableCell>{r.date}</TableCell>
            <TableCell className="text-right">{r.pos_net_sales == null ? 'POS raporu yüklenmedi' : tl(r.pos_net_sales)}</TableCell>
            <TableCell className="text-right">{r.erp_revenue == null ? 'ERP cirosu eksik' : tl(r.erp_revenue)}</TableCell>
            <TableCell className="text-right">{r.diff == null ? '—' : tl(r.diff)}</TableCell>
            <TableCell className="text-right">{pctT(r.diff_pct)}</TableCell>
            <TableCell><Badge variant={r.status === 'eşleşti' ? 'outline' : 'secondary'}>{r.status}</Badge></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const PosSection = ({ data }) => {
  const A = data.period_a.pos, B = data.period_b.pos;
  const warns = [...A.warnings, ...B.warnings];
  const recon = [...A.reconciliation, ...B.reconciliation];
  return (
    <Card data-testid="pos-section">
      <CardHeader className="pb-2"><CardTitle className="text-base">Orion POS satışları (ayrı veri kaynağı – ERP cirosuyla toplanmaz)</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-muted-foreground">Önceki POS net</p><p className="text-lg font-semibold" data-testid="pos-a-net">{A.net_sales_total == null ? 'POS raporu yok' : tl(A.net_sales_total)}</p><p className="text-xs">POS kapsama %{A.coverage_pct} ({A.daily_report_days}/{A.days} gün)</p></div>
          <div><p className="text-muted-foreground">Yeni POS net</p><p className="text-lg font-semibold" data-testid="pos-b-net">{B.net_sales_total == null ? 'POS raporu yok' : tl(B.net_sales_total)}</p><p className="text-xs">POS kapsama %{B.coverage_pct} ({B.daily_report_days}/{B.days} gün)</p></div>
          <div><p className="text-muted-foreground">Değişim</p><p className="text-lg">{data.pos.pct == null ? <Badge variant="outline">Güvenilmez – eksik rapor</Badge> : pctT(data.pos.pct)}</p></div>
          <div><p className="text-muted-foreground">Yeni dönem brüt / indirim</p><p>{tl(B.gross_menu_total)} / {tl(B.discount_total)}</p><p className="text-xs">Hesap {B.checks ?? '—'} · Kuver {B.covers ?? '—'} · POS harcama {tl(B.pos_expenses_total)}</p></div>
        </div>
        <div>
          <p className="text-sm font-medium mb-1">Ciro mutabakatı (POS ↔ ERP günlük ciro)</p>
          <p className="text-xs text-muted-foreground mb-1" data-testid="pos-recon-direction">Fark = ERP ciro − POS net satış. Pozitif: ERP'ye girilen ciro POS'tan fazla; negatif: ERP cirosu POS'tan az.</p>
          {recon.length === 0 ? <p className="text-sm text-muted-foreground">Seçilen tarihlerde ne POS raporu ne ERP cirosu var.</p> : <ReconTable rows={recon} />}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MiniTable title="Ödeme yöntemleri (yeni dönem)" rows={B.payments} total={B.net_sales_total} testid="pos-payments-summary" />
          <MiniTable title="Sipariş türleri (yeni dönem; ödeme yönteminden ayrı)" rows={B.order_types} total={B.net_sales_total} testid="pos-order-summary" />
        </div>
        <div data-testid="pos-periods-chart">
          <p className="text-sm font-medium mb-1">İşlem periyotları (Orion 2 saatlik dönemleri, yeni dönem)</p>
          {B.periods.length === 0 ? <p className="text-sm text-muted-foreground">POS raporu yüklenmedi</p> : (
            <div className="h-56"><ResponsiveContainer>
              <BarChart data={B.periods}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="label" fontSize={11} /><YAxis tickFormatter={short} fontSize={11} width={45} /><Tooltip formatter={(v) => tl(v)} /><Bar dataKey="amount" name="Satış" fill="#c2410c" /></BarChart>
            </ResponsiveContainer></div>
          )}
          <p className="text-xs text-muted-foreground mt-1">Saatlik dağılım uydurulmadı; dönemler raporda olduğu gibi gösterilir.</p>
        </div>
        {warns.length > 0 && (
          <div className="space-y-1 text-sm" data-testid="pos-warnings">
            {warns.map((w, i) => (<div key={i} className="flex gap-2">{w.level === 'warning' ? <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" /> : <Info size={16} className="text-blue-600 shrink-0 mt-0.5" />}<span>{w.message}</span></div>))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MappingRow = ({ p, recipes, canEdit, onSaved }) => {
  const [rid, setRid] = useState(p.mapping?.recipe_id || p.suggested_recipe_id || '');
  const sel = recipes.find((r) => r.id === rid);
  const save = async (status) => {
    if (status === 'confirmed') {
      const msg = `${p.product_code} ${p.product_name}\n→ Reçete: ${sel?.name}\nPorsiyon başı maliyet: ${tl(sel?.cost_per_portion)} (reçete ${sel?.portions_per_recipe} porsiyon, toplam ${tl(sel?.total_cost)})\nYarım/1,5 porsiyon satırlarında maliyet katsayıyla çarpılır; ikramlarda da maliyet hesaplanır.\n\nEşleştirme kaydedilsin mi?`;
      if (!window.confirm(msg)) return;
    }
    try {
      await api.put(`/pos-mappings/${encodeURIComponent(p.product_code)}`, { status, recipe_id: status === 'confirmed' ? rid : null });
      toast.success(`${p.product_name}: kaydedildi`);
      onSaved();
    } catch (e) { toast.error(errText(e)); }
  };
  const st = p.mapping?.status;
  return (
    <TableRow data-testid={`pos-map-row-${p.product_code}`}>
      <TableCell className="font-data">{p.product_code}</TableCell>
      <TableCell className="max-w-[180px] break-words">{p.product_name}{p.is_addition && <Badge variant="outline" className="ml-1">İlave</Badge>}<div className="text-xs text-muted-foreground">{p.variants.join(', ')}</div></TableCell>
      <TableCell className="text-right">{tl(p.amount)}</TableCell>
      <TableCell>
        {st === 'confirmed' ? <Badge>Onaylı: {p.mapping.recipe_name}</Badge> : st === 'ignored' ? <Badge variant="secondary">Maliyetsiz</Badge> : p.suggested_recipe_name ? <span className="text-xs">Öneri: {p.suggested_recipe_name} ({p.suggestion_reason})</span> : <span className="text-xs text-muted-foreground">Eşleşmedi</span>}
      </TableCell>
      <TableCell>
        {canEdit && (
          <div className="flex flex-wrap gap-1 items-center">
            <select className="border border-border rounded-md bg-background text-xs px-1 py-1 max-w-[160px]" value={rid} onChange={(e) => setRid(e.target.value)} data-testid={`pos-map-select-${p.product_code}`}>
              <option value="">Reçete seç…</option>
              {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {sel && <span className="text-xs text-muted-foreground w-full" data-testid={`pos-map-detail-${p.product_code}`}>{sel.name} · porsiyon başı {tl(sel.cost_per_portion)} · reçete {sel.portions_per_recipe} porsiyon</span>}
            <Button size="sm" variant="outline" disabled={!rid} onClick={() => save('confirmed')} data-testid={`pos-map-confirm-${p.product_code}`}>Onayla</Button>
            <Button size="sm" variant="ghost" onClick={() => save('ignored')} data-testid={`pos-map-ignore-${p.product_code}`}>Maliyetsiz</Button>
            {st && <Button size="sm" variant="ghost" onClick={() => save('unmapped')} data-testid={`pos-map-clear-${p.product_code}`}>Kaldır</Button>}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export const PosMappingAndContribution = ({ start, end }) => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('smart_analytics', 'create');
  const [maps, setMaps] = useState(null);
  const [contrib, setContrib] = useState(null);
  const [error, setError] = useState(null);
  const load = async () => {
    setError(null);
    try {
      const [m, c] = await Promise.all([api.get('/pos-mappings', { timeout: 90000 }), api.get('/pos-analytics/contribution', { params: { start, end }, timeout: 90000 })]);
      setMaps(m.data); setContrib(c.data);
    } catch (e) { setError(errText(e)); }
  };
  useEffect(() => { load(); }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <div className="p-3 border border-destructive rounded-lg text-sm text-destructive flex gap-3 items-center" data-testid="pos-contrib-error">{error}<Button size="sm" variant="outline" onClick={load}>Yeniden Dene</Button></div>;
  if (!maps || !contrib) return <p className="text-sm text-muted-foreground">POS eşleştirme yükleniyor...</p>;
  const t = contrib.totals;
  return (
    <div className="space-y-6">
      <Card data-testid="pos-contribution">
        <CardHeader className="pb-2"><CardTitle className="text-base">Tahmini brüt ürün katkısı ({start} – {end})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div><p className="text-muted-foreground">Eşleşen ürün satışı</p><p className="font-semibold" data-testid="pos-contrib-matched-sales">{tl(t.sales_matched)}</p></div>
            <div><p className="text-muted-foreground">Tahmini reçete maliyeti</p><p className="font-semibold">{tl(t.est_cost_matched)}</p></div>
            <div><p className="text-muted-foreground">Tahmini brüt katkı</p><p className="font-semibold" data-testid="pos-contrib-total">{tl(t.contribution_matched)}</p></div>
            <div><p className="text-muted-foreground">İkram maliyeti (eşleşenler)</p><p className="font-semibold" data-testid="pos-contrib-ikram">{tl(t.complimentary_cost)}</p></div>
            <div><p className="text-muted-foreground">Eşleşmeyen satış (maliyet atanmadı)</p><p className="font-semibold">{tl(t.unmatched_sales)} · {t.unmatched_rows} satır</p></div>
          </div>
          <div className="max-h-72 overflow-auto border rounded-lg">
            <Table className="text-sm">
              <TableHeader><TableRow><TableHead>Ürün</TableHead><TableHead>Varyasyon</TableHead><TableHead className="text-right">Adet</TableHead><TableHead className="text-right">Satış</TableHead><TableHead className="text-right">Porsiyon</TableHead><TableHead className="text-right">Tahmini maliyet</TableHead><TableHead className="text-right">Brüt katkı</TableHead><TableHead>Durum</TableHead></TableRow></TableHeader>
              <TableBody>
                {contrib.rows.map((r, i) => (
                  <TableRow key={i}><TableCell className="max-w-[180px] break-words">{r.product_code} {r.product_name}</TableCell><TableCell className="text-xs">{r.variant}</TableCell><TableCell className="text-right">{r.quantity}</TableCell><TableCell className="text-right">{tl(r.sales)}</TableCell><TableCell className="text-right text-xs">x{String(r.portion).replace('.', ',')}{r.is_complimentary ? ' ikram' : ''}</TableCell><TableCell className="text-right">{tl(r.est_cost)}</TableCell><TableCell className="text-right">{tl(r.contribution)}</TableCell><TableCell className="text-xs">{r.status}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="text-xs text-muted-foreground list-disc pl-5">{contrib.notes.map((n) => <li key={n}>{n}</li>)}</ul>
        </CardContent>
      </Card>
      <Card data-testid="pos-mapping">
        <CardHeader className="pb-2"><CardTitle className="text-base">POS – reçete eşleştirme ({maps.products.filter((p) => !p.mapping).length} eşleşmemiş / {maps.products.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">Öneriler otomatik kaydedilmez; yönetici onayı gerekir. Eşleştirme ürün koduna yapılır ve tüm varyasyonlara (yarım, 1,5, ikram) uygulanır. Reçeteler değiştirilmez.</p>
          <div className="max-h-96 overflow-auto border rounded-lg">
            <Table className="text-sm">
              <TableHeader><TableRow><TableHead>Kod</TableHead><TableHead>POS ürünü</TableHead><TableHead className="text-right">Satış</TableHead><TableHead>Durum</TableHead><TableHead>İşlem</TableHead></TableRow></TableHeader>
              <TableBody>
                {maps.products.map((p) => <MappingRow key={p.product_code} p={p} recipes={maps.recipes} canEdit={canEdit} onSaved={load} />)}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
